import React, {useCallback, useEffect, useMemo, useState} from "react";
import "./SearchControls.css";
import {
  AdvancedMarker,
  ControlPosition,
  MapControl,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import {useAutocompleteSuggestions} from "../logic/useAutocompleteSuggestions";
import Combobox from "react-widgets/Combobox";
import "react-widgets/styles.css";

function SearchControls() {
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.Place | null>(null);

  return (
    <>
      <AutocompleteResult place={selectedPlace} />
      <MapControl position={ControlPosition.TOP_RIGHT}>
        <AutocompleteInput onPlaceSelect={(place) => setSelectedPlace(place)} />
      </MapControl>
    </>
  );
}

const AutocompleteInput = ({
  onPlaceSelect,
}: {
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
}) => {
  const [inputValue, setInputValue] = useState<string>("");

  const {suggestions, resetSession, isLoading} = useAutocompleteSuggestions(
    inputValue,
    {locationBias: {lat: 49.79, lng: 9.95}},
  );

  const handleInputChange = useCallback(
    (value: google.maps.places.PlacePrediction | string) => {
      if (typeof value === "string") {
        setInputValue(value);
      }
    },
    [],
  );

  const handleSelect = useCallback(
    (prediction: google.maps.places.PlacePrediction | string) => {
      if (typeof prediction === "string") return;

      const place = prediction.toPlace();
      place
        .fetchFields({
          fields: [
            "viewport",
            "location",
            "svgIconMaskURI",
            "iconBackgroundColor",
          ],
        })
        .then(() => {
          resetSession();
          onPlaceSelect(place);
          setInputValue("");
        });
    },
    [onPlaceSelect],
  );

  const predictions = useMemo(
    () =>
      suggestions
        .filter((suggestion) => suggestion.placePrediction)
        .map(({placePrediction}) => placePrediction!),
    [suggestions],
  );

  return (
    <div className="searchControls__placeSearch">
      <Combobox
        placeholder="Nach Orten suchen"
        data={predictions}
        dataKey="placeId"
        textField="text"
        value={inputValue}
        onChange={handleInputChange}
        onSelect={handleSelect}
        busy={isLoading}
        filter={() => true}
        focusFirstItem={true}
        hideEmptyPopup
        hideCaret
      />
    </div>
  );
};

const AutocompleteResult = ({
  place,
}: {
  place: google.maps.places.Place | null;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !place) return;
    if (place.viewport) map.fitBounds(place.viewport);
  }, [map, place]);

  if (!place) return null;

  return (
    <AdvancedMarker position={place.location}>
      <Pin
        background={place.iconBackgroundColor}
        glyph={place.svgIconMaskURI ? new URL(place.svgIconMaskURI) : null}
      />
    </AdvancedMarker>
  );
};

export default SearchControls;
